// ═══════════════════════════════════════════════════════════════════════════
// Per-action MATCHPROP translators (CC-2c PR-1) — PACKAGE-INTERNAL
// ═══════════════════════════════════════════════════════════════════════════
//
// The thin, total per-action translator behind the MATCHPROP domain
// (MATCH_PROPERTIES). The AI shape maps 1:1 onto the TWO reducer `EditorCommand`s
// MATCHPROP is composed of — with no simplification: it arms the donor
// (`matchprop/set-source`) then paints the batch (`matchprop/apply`), the exact
// two-step the human MATCHPROP / CLI path dispatches.
//
// IMPORTANT (AI safe contour): this translator produces the property-mutating
// `matchprop/apply` `EditorCommand` WITHOUT running the CC-2a fail-closed guard
// (`assertAiCallableMatchpropAction`). It is therefore PACKAGE-INTERNAL: this
// file is NOT re-exported by the barrel (`index.ts`). The ONLY public MATCHPROP
// entry is the guarded `translateMatchpropCommand` in `matchpropDispatcher.ts`
// (which imports this). This mirrors the DRAW-MODIFY domain (per-action
// translators in non-barrelled `drawModifyTranslators.ts`) and the VIEW+SPACE
// domain (`viewSpaceTranslators.ts`) — so a consumer of
// `@dxf-ai-editor/ai-commands` can never emit `matchprop/apply` past the
// fail-closed guard. Honours the CLAUDE.md rules:
//   "Properties, CHPROP, MATCHPROP и OSNAP — это системные механизмы." +
//   "AI drafting … может только предлагать и исполнять команды через безопасный
//    контур."

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import { AI_COMMAND_SOURCE } from "./blockDispatcherShared";
import type { MatchPropertiesAiCommand } from "./matchpropSchemas";

// ─── MATCH_PROPERTIES (MATCHPROP / MA) ─────────────────────────────
//
// Emits the atomic MATCHPROP pair: `matchprop/set-source` (arm the donor id)
// followed by `matchprop/apply` (paint the property `mode` onto the target
// batch). Both halves are required — the reducer only paints when a source is
// armed, so the pair is dispatched together (the app wraps them in one
// grouped-undo unit). `mode` is always concrete here (the schema defaults it to
// "all"), so the copied property groups are unambiguous. The target array is
// copied defensively so the emitted command never aliases the AI proposal.
export function translateMatchProperties(
  command: MatchPropertiesAiCommand,
): EditorCommand[] {
  return [
    {
      type: "matchprop/set-source",
      source: AI_COMMAND_SOURCE,
      entityId: command.source,
    },
    {
      type: "matchprop/apply",
      source: AI_COMMAND_SOURCE,
      targetEntityIds: [...command.targets],
      mode: command.mode,
    },
  ];
}
